import React, { useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { useMutation } from 'react-query'

import { useApi } from '../../../hooks'
import toast from '../../toast'

const VehicleHeader = ({ report, allowViewPricing, refetch }) => {
  const history = useHistory()
  const { adasReportService } = useApi()
  const [showModal, setShowModal] = useState(false)
  const [calibrationReady, setCalibrationReady] = useState('')
  const [note, setNote] = useState('')

  const v = report.vehicle || {}
  const vehicle = [v.year, v.make, v.model].filter(Boolean).join(' ')
  const client = report.client || {}
  const insurance = (report.checkIn && report.checkIn.insurance) || {}
  const conversion = report.conversion || {}
  const stockNumber = report.stockNumber || ''
  const currentReady = report.clientPortal && report.clientPortal.calibrationReady
  const totalPrice = report.totalPrice || 0

  const mutation = useMutation(
    () => adasReportService.updateReadiness({ id: report._id, calibrationReady, note }),
    {
      retry: false,
      onSuccess: () => {
        toast.success({ title: 'Updated', body: 'Calibration readiness updated.' })
        setShowModal(false)
        setCalibrationReady('')
        setNote('')
        refetch()
      },
      onError: (err) => {
        toast.error({ title: 'Error', body: err.message || 'Failed to update readiness.' })
        setShowModal(false)
      },
    }
  )

  const conversionPath = () => {
    if (!conversion.documentId) return null
    if (conversion.convertedTo === 'invoice') return '/invoices/' + conversion.documentId
    if (conversion.convertedTo === 'estimate') return '/estimates/' + conversion.documentId
    if (conversion.convertedTo === 'work-order') return '/work-orders/' + conversion.documentId
    return null
  }

  const handleViewClick = () => {
    const path = conversionPath()
    if (!path) {
      toast.error({ title: 'Not Available', body: 'No linked document found.' })
      return
    }
    toast.info({ title: 'Coming Soon', body: 'Document viewing is not yet available.' })
  }

  const handleCalibrationClick = () => {
    setShowModal(true)
  }

  const clientStreet = client.street || ''
  const clientCityStateZip = [client.city, client.state, client.zip].filter(Boolean).join(', ')

  return (
    <>
      <div className='adas-detail__vehicle-card'>
        <div className='adas-detail__vehicle-header'>
          <h2 className='adas-detail__vehicle-title'>{vehicle || 'Unknown Vehicle'}</h2>
          <div className='adas-detail__action-buttons'>
            {conversionPath() && (
              <button className='btn-outline-view' onClick={handleViewClick}>
                VIEW
              </button>
            )}
            {currentReady === 'yes' ? (
              <span className='calibration-badge'>READY FOR CALIBRATION</span>
            ) : (
              <button className='btn-primary-calibration' onClick={handleCalibrationClick}>
                READY FOR CALIBRATION
              </button>
            )}
          </div>
        </div>

        <div className='adas-detail__vehicle-content'>
          <div className='adas-detail__vehicle-details'>
            <div className='adas-detail__info-column'>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Color:</span>
                <span className='adas-detail__info-value'>{v.color || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Trim:</span>
                <span className='adas-detail__info-value'>{v.trim || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>VIN:</span>
                <span className='adas-detail__info-value'>{v.vin || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>STOCK/RO:</span>
                <span className='adas-detail__info-value'>{stockNumber || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Insurance Carrier:</span>
                <span className='adas-detail__info-value'>{insurance.name || '—'}</span>
              </div>
              <div className='adas-detail__info-row'>
                <span className='adas-detail__info-label'>Insurance Pricing:</span>
                <span className='adas-detail__info-value adas-detail__muted-text'>
                  {insurance.pricing || 'Not Selected'}
                </span>
              </div>
            </div>

            <div className='adas-detail__divider' />
            <div className='adas-detail__client-info'>
              {client.name && <div>{client.name}</div>}
              {clientStreet && <div>{clientStreet}</div>}
              {clientCityStateZip && <div>{clientCityStateZip}</div>}
              {client.phone && <div>{client.phone}</div>}
              {client.email && <div>{client.email}</div>}
            </div>
          </div>

          {allowViewPricing && (
            <div className='adas-detail__total'>
              <span className='adas-detail__total-label'>Total</span>
              <span className='adas-detail__total-amount'>
                ${Number(totalPrice).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Calibration Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Ready for Calibration</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className='fw-bold mb-2'>Is the vehicle ready for calibration?</p>
          <Form.Check
            type='radio'
            label='Yes'
            name='calibrationReady'
            id='cal-yes'
            checked={calibrationReady === 'yes'}
            onChange={() => setCalibrationReady('yes')}
          />
          <Form.Check
            type='radio'
            label='No'
            name='calibrationReady'
            id='cal-no'
            checked={calibrationReady === 'no'}
            onChange={() => setCalibrationReady('no')}
            className='mt-1'
          />
          <Form.Group className='mt-3'>
            <Form.Label>Note (optional)</Form.Label>
            <Form.Control
              as='textarea'
              rows={3}
              value={note}
              onChange={e => setNote(e.target.value)}
              placeholder='Add a comment...'
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant='secondary' onClick={() => setShowModal(false)}>Cancel</Button>
          <Button
            variant='primary'
            disabled={!calibrationReady || mutation.isLoading}
            onClick={() => mutation.mutate()}
          >
            {mutation.isLoading ? 'Submitting...' : 'Submit'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}

export default VehicleHeader
